import { getMic } from "@/lib/mic";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useOrbVoice } from "@/lib/orbVoice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MistOrb from "@/components/MistOrb";
import { Send, Mic, Square, ArrowRight, Loader2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const MAX_TURNS = 5;
const orbSize = () => Math.min(typeof window !== "undefined" ? window.innerWidth * 0.62 : 280, 300);

function Reflection({ reflection, loading, amplitudeRef, speaking, onGo, onEnter }) {
  const p = reflection?.profile;
  const step = reflection?.first_step || p?.first_step;
  return (
    <div className="min-h-screen bg-[#070a14] flex flex-col items-center px-6 py-10 relative overflow-hidden" data-testid="onboarding-reflection">
      <MistOrb size={Math.min(orbSize(), 200)} amplitudeRef={amplitudeRef} state={loading ? "thinking" : speaking ? "speaking" : "idle"} />
      {loading || !reflection ? (
        <div className="flex items-center gap-2 text-white/60 mt-8"><Loader2 className="w-4 h-4 animate-spin" /> Your Coach is reflecting…</div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-lg w-full mt-6">
          <p className="text-xs uppercase tracking-[0.25em] text-primary mb-4 text-center">I think I understand</p>
          <p className="font-serif text-2xl sm:text-3xl leading-snug mb-8 text-center text-white/95" data-testid="onboarding-reflection-text">{reflection.reflection}</p>
          <div className="grid gap-3 mb-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><span className="text-[10px] uppercase tracking-widest text-primary">Your natural strength</span><p className="text-sm mt-1 text-white/85">{p.strengths}</p></div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"><span className="text-[10px] uppercase tracking-widest text-primary">Your growth edge</span><p className="text-sm mt-1 text-white/85">{p.growth_edge}</p></div>
          </div>
          {step && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="rounded-3xl border border-primary/40 bg-primary/[0.07] p-6 mb-6" data-testid="onboarding-first-step">
              <span className="text-[10px] uppercase tracking-widest text-primary">Your first step</span>
              <p className="font-serif text-2xl mt-1 text-white">{step.title}</p>
              <p className="text-sm text-white/70 mt-2">{step.why}</p>
              <Button className="rounded-full mt-5 group" onClick={() => onGo(step.route)} data-testid="onboarding-first-step-btn">
                {step.cta} <ArrowRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </motion.div>
          )}
          <button onClick={onEnter} className="w-full text-sm text-white/60 hover:text-white transition-colors" data-testid="onboarding-enter">Or enter Xcelio →</button>
        </motion.div>
      )}
    </div>
  );
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { amplitudeRef, speak, stop } = useOrbVoice("sage");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [ready, setReady] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("xcelio_onboard_voice") !== "off");
  const [phase, setPhase] = useState("chat");
  const [reflection, setReflection] = useState(null);
  const [recording, setRecording] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const bootRef = useRef(false);
  const voiceOnRef = useRef(voiceOn);
  voiceOnRef.current = voiceOn;

  const say = async (text) => {
    if (!voiceOnRef.current || !text) return;
    setSpeaking(true); await speak(text); setSpeaking(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (bootRef.current || user?.welcome_pending) return; bootRef.current = true; send(""); }, [user?.welcome_pending]); // wait for Adelaida's welcome to finish

  const send = async (text) => {
    if (sending) return;
    stop(); setSpeaking(false);
    if (text) setMessages((m) => [...m, { role: "user", text }]);
    setInput(""); setSending(true);
    try {
      const { data } = await api.post("/onboarding/message", { text });
      setMessages(data.history || ((m) => [...m, { role: "mirro", text: data.reply }]));
      setReady(data.ready);
      setSending(false);
      say(data.reply);
    } catch { toast.error("Connection hiccup — try again."); setSending(false); }
  };

  const finalize = async () => {
    stop(); setPhase("reflection"); setSending(true);
    try {
      const { data } = await api.post("/onboarding/finalize");
      setReflection(data); setSending(false);
      say(data.reflection);
    } catch { toast.error("Could not finish onboarding."); setPhase("chat"); setSending(false); }
  };

  const leave = async (route) => { stop(); await refreshUser(); navigate(route); };
  const skip = async () => { stop(); try { await api.post("/onboarding/finalize"); await refreshUser(); } catch {} navigate("/app"); };
  const toggleVoice = () => { const v = !voiceOn; setVoiceOn(v); localStorage.setItem("xcelio_onboard_voice", v ? "on" : "off"); if (!v) { stop(); setSpeaking(false); } };

  const startRec = async () => {
    try {
      stop(); setSpeaking(false);
      const stream = await getMic();
      const mr = new MediaRecorder(stream); mrRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData(); fd.append("file", new Blob(chunksRef.current, { type: "audio/webm" }), "a.webm");
        try { const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }); if (data.text?.trim()) send(data.text.trim()); }
        catch { toast.error("Couldn't hear that."); }
      };
      mr.start(); setRecording(true);
    } catch { }
  };
  const stopRec = () => { mrRef.current?.stop(); setRecording(false); };

  if (phase === "reflection") {
    return <Reflection reflection={reflection} loading={sending} amplitudeRef={amplitudeRef} speaking={speaking} onGo={leave} onEnter={() => leave("/app")} />;
  }

  const userTurns = messages.filter((m) => m.role === "user").length;
  const coachMsgs = messages.filter((m) => m.role === "mirro");
  const lastCoach = coachMsgs[coachMsgs.length - 1];
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const orbState = sending ? "thinking" : speaking ? "speaking" : recording ? "listening" : "idle";

  return (
    <div className="min-h-screen bg-[#070a14] flex flex-col relative overflow-hidden" data-testid="onboarding-screen">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 60% at 50% 30%, rgba(60,80,120,0.25), transparent 70%)" }} />
      <div className="relative z-10 max-w-xl w-full mx-auto flex-1 flex flex-col px-5 pt-6 pb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-serif text-xl text-white/95">Meet your Coach</div>
            <div className="flex gap-1.5 mt-2" data-testid="onboarding-progress">
              {Array.from({ length: MAX_TURNS }).map((_, i) => (
                <span key={i} className={`h-1 w-6 rounded-full transition-colors duration-500 ${i < userTurns ? "bg-primary" : "bg-white/15"}`} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={toggleVoice} data-testid="onboarding-voice-toggle" aria-label={voiceOn ? "Mute coach voice" : "Unmute coach voice"}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20 transition-colors">
              {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button type="button" onClick={skip} data-testid="onboarding-skip" className="text-xs text-white/50 hover:text-white flex items-center gap-1">
              Skip <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center py-4">
          <MistOrb size={orbSize()} amplitudeRef={amplitudeRef} state={orbState} />
          <div className="min-h-[140px] mt-6 w-full text-center" data-testid="onboarding-messages">
            {lastUser && <p className="text-xs text-white/40 mb-3 line-clamp-2 italic" data-testid="onboarding-user">“{lastUser.text}”</p>}
            <AnimatePresence mode="wait">
              {lastCoach && !sending && (
                <motion.p key={coachMsgs.length} initial={{ opacity: 0, y: 10, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0 }} transition={{ duration: 0.7 }}
                  className="font-serif text-2xl sm:text-[28px] leading-snug text-white/95" data-testid="onboarding-mirro">{lastCoach.text}</motion.p>
              )}
            </AnimatePresence>
            {sending && <p className="text-xs uppercase tracking-[0.25em] text-white/40" data-testid="onboarding-thinking">Listening deeply…</p>}
          </div>
        </div>

        {(ready || userTurns >= 3) && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
            <Button className="w-full rounded-full" onClick={finalize} disabled={sending} data-testid="onboarding-finalize">See what your Coach understood</Button>
          </motion.div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); if (input.trim()) send(input.trim()); }} className="flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={recording ? "Listening…" : "Reply, or tap the mic…"} disabled={recording}
            data-testid="onboarding-input" className="rounded-full bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          <Button type="button" size="icon" variant={recording ? "destructive" : "outline"} className="rounded-full shrink-0 border-white/15" onClick={recording ? stopRec : startRec} data-testid="onboarding-mic">
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Button type="submit" size="icon" className="rounded-full shrink-0" disabled={!input.trim() || sending} data-testid="onboarding-send"><Send className="w-4 h-4" /></Button>
        </form>
      </div>
    </div>
  );
}
